import axios from "axios";
import { SERVER_BASE_URL, SOL_SERVER_BASE_URL } from "../env";
import {networkStatusStore} from "../react/hooks/useNetworkState.jsx";
import { sendCustomMessageOnSentry } from "../sentryIntegration.jsx";
import { getAuthToken } from "./tokenManager.js";

const apiAgent = axios.create({
  baseURL: SERVER_BASE_URL,
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json;charset=UTF-8",
  },
  withCredentials: true
});

const apiSolAgent = axios.create({
  baseURL: SOL_SERVER_BASE_URL,
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json;charset=UTF-8",
  },
  withCredentials: false  // Don't send cookies to Sol server
});

// Request interceptor for Sol API: Add Bearer token and track network status
apiSolAgent.interceptors.request.use(
  (config) => {
    networkStatusStore.updateNetworkRequestStatus(true);
    if (config.method !== "get") window.swivuInProgressNetworkRequests++;
    // Get token from token manager
    const token = getAuthToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    networkStatusStore.updateNetworkRequestStatus(false);
    return Promise.reject(error);
  }
);


export const install401Interceptor = (interceptor) => {
  apiAgent.interceptors.response.use(null, (error) => {
    if (error?.response?.status === 401) {
      interceptor(error);
    }
    return Promise.reject(error);
  });
}

export const install403And404Interceptor = (interceptor) => {
  apiAgent.interceptors.response.use(null, (error) => {
    if (error?.config?.skipAccessDeniedToast) {
      return Promise.reject(error);
    }
    if (error?.response?.status === 403 || error?.response?.status === 404 ) {
      interceptor(error);
    }
    return Promise.reject(error);
  });
}

const CancelToken = axios.CancelToken;

apiAgent.interceptors.request.use((config) => {
  networkStatusStore.updateNetworkRequestStatus(true);
  if (config.method !== "get") window.swivuInProgressNetworkRequests++;
  return config;
});

apiAgent.interceptors.response.use(
  (response) => {
    networkStatusStore.updateNetworkRequestStatus(false);
    if (response.config.method !== "get") window.swivuInProgressNetworkRequests--;
    return response;
  },
  (error) => {
    networkStatusStore.updateNetworkRequestStatus(false);
    if (error.config && error.config.method !== "get") window.swivuInProgressNetworkRequests--;

    // External API calls logging to sentry
    const status = error.response?.status || "N/A";
    if (status !== 401) {
      sendCustomMessageOnSentry({
        messageTitle: `${error.config?.method?.toUpperCase()} ${error.config?.url || "Unknown URL"} - ${status}`,
        logType: "error",
        extraDataBody: {
          responseMessage: `${status}${error.message ? `: ${error.message}` : ""}`,
          requestData: error.config?.data,
          responseData: error.response?.data,
          responseOptions: error.response?.options
        }
      })
    }
    return Promise.reject(error);
  }
);

// Response interceptor for Sol API
apiSolAgent.interceptors.response.use(
  (response) => {
    networkStatusStore.updateNetworkRequestStatus(false);
    if (response.config.method !== "get") window.swivuInProgressNetworkRequests--;
    return response;
  },
  (error) => {
    networkStatusStore.updateNetworkRequestStatus(false);
    if (error.config && error.config.method !== "get") window.swivuInProgressNetworkRequests--;

    // Log Sol API errors to sentry
    const status = error.response?.status || "N/A";
    if (status !== 401) {
      sendCustomMessageOnSentry({
        messageTitle: `[Sol API] ${error.config?.method?.toUpperCase()} ${error.config?.url || "Unknown URL"} - ${status}`,
        logType: "error",
        extraDataBody: {
          responseMessage: `${status}${error.message ? `: ${error.message}` : ""}`,
          requestData: error.config?.data,
          responseData: error.response?.data,
          responseOptions: error.response?.options
        }
      })
    }
    return Promise.reject(error);
  }
);

export {
  apiAgent,
  apiSolAgent,
  CancelToken,
};
